/**
 * Module-level registry for long-running, fire-once generation requests
 * (insights, EBITDA bridge). The promise for an in-flight POST lives here,
 * NOT in component state, so switching tabs unmounts the view but never
 * abandons the request: the fetch keeps running, the server persists the
 * result, and a remounted tab re-attaches to the same promise mid-flight.
 *
 * Results are deliberately NOT cached here — the server caches them (Redis +
 * view_state) and invalidates on category edits / new runs, so tabs always
 * re-GET on mount and stay correct. Only the in-flight promise and the last
 * failure are held. A remembered failure keeps a revisited tab on the error
 * state instead of silently re-firing an expensive LLM call; starting a new
 * job (Try again / Re-run) clears it.
 */

const inflight = new Map<string, Promise<unknown>>();
const lastError = new Map<string, string>();

export function getInflightJob<T>(key: string): Promise<T> | null {
  return (inflight.get(key) as Promise<T> | undefined) || null;
}

export function getJobError(key: string): string | null {
  return lastError.get(key) || null;
}

export function startJob<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;
  lastError.delete(key);
  const tracked = fn().then(
    (res) => {
      inflight.delete(key);
      return res;
    },
    (err) => {
      inflight.delete(key);
      lastError.set(
        key,
        (err && (err as Error).message) || 'Generation failed.',
      );
      throw err;
    },
  );
  inflight.set(key, tracked);
  // A rejection while no tab is mounted must not surface as an unhandled
  // rejection; mounted tabs attach their own handlers.
  tracked.catch(() => {});
  return tracked;
}
